import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Navigation, Loader2, AlertTriangle, Settings, X, ShieldAlert, Map } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import useGpsLocation from '@/hooks/useGpsLocation';
import useGeolocation from '@/hooks/useGeolocation';
import useApi from '@/utils/useApi';
import apis from '@/utils/apis';
import useUser from '@/hooks/useUser';
import { setGpsLocation } from '@/store/slices/userSlice';
import MapLocationPicker from './MapLocationPicker';

const LocationGateModal = ({ isOpen, locationChanged, isDismissable = false, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isNative = Capacitor.isNativePlatform();

  // GPS hooks
  const { getLocation } = useGpsLocation();
  const {
    checkLocationEnabled,
    requestEnableLocation,
    ensureLocationPermission,
    goToSettingsAndWait,
  } = useGeolocation();

  // API hooks
  const { callApi: callNearestNearbyApi } = useApi();
  const { callApi: callCreateAddressApi } = useApi();
  const { fetchUserDetails } = useUser();

  // State
  const [gpsState, setGpsState] = useState('idle'); // idle | detecting | denied | no-area | error
  const [isPermanentlyDenied, setIsPermanentlyDenied] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isWatchingPermission, setIsWatchingPermission] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [locationOff, setLocationOff] = useState(false);
  const [lastCoords, setLastCoords] = useState(() => {
    try {
      const saved = localStorage.getItem('lastSearchLocation');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const persistCoords = useCallback((coords) => {
    setLastCoords(coords);
    try { localStorage.setItem('lastSearchLocation', JSON.stringify(coords)); } catch { /* quota exceeded */ }
  }, []);

  const permWatcherRef = useRef(null);
  const handleUseCurrentLocationRef = useRef(null);

  // ─── Save address helper ─────────────────────────────────────────────────────
  const saveAddress = useCallback(
    async (loc, showToast = false) => {
      await callCreateAddressApi({
        api: apis.createUserAddress,
        body: {
          city: loc.city,
          state: loc.state,
          country: loc.country,
          pin_code: loc.pinCode?.toString(),
          nearby: loc._id,
          is_default: true,
          location: loc.location?.coordinates
            ? { coordinates: loc.location.coordinates }
            : undefined,
        },
      });
      localStorage.setItem('defaultLocationId', loc._id);
      await fetchUserDetails();
      if (showToast) {
        toast.success(
          `${loc.nearbyLocation}${loc.pinCode ? ` · ${loc.pinCode}` : ''}`,
          { icon: '📍' }
        );
      }
      onSuccess();
    },
    [callCreateAddressApi, fetchUserDetails, onSuccess],
  );

  // ─── Nearest NearbyLocation lookup ──────────────────────────────────────────
  const findNearest = useCallback(
    async (latitude, longitude) => {
      const result = await callNearestNearbyApi({
        api: {
          url: `${apis.getNearestNearbyLocation.url}?latitude=${latitude}&longitude=${longitude}`,
          method: apis.getNearestNearbyLocation.method,
        },
      });
      if (result?.found && result?.nearbyLocation) {
        await saveAddress(result.nearbyLocation, true);
      } else {
        setGpsState('no-area');
      }
    },
    [callNearestNearbyApi, saveAddress],
  );

  // ─── Browser permission watcher (PWA only) ───────────────────────────────────
  // Stops and removes any active Permissions API listener
  const stopPermWatch = useCallback(() => {
    if (permWatcherRef.current) {
      permWatcherRef.current.onchange = null;
      permWatcherRef.current = null;
      setIsWatchingPermission(false);
    }
  }, []);

  // Starts watching the browser's geolocation permission state.
  // When the user opens the lock icon and grants permission, auto-retries GPS.
  const startPermWatch = useCallback(() => {
    if (!navigator.permissions || isNative) return;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permResult) => {
        if (permResult.state === 'granted') return; // already granted, nothing to watch
        permWatcherRef.current = permResult;
        setIsWatchingPermission(true);
        permResult.onchange = () => {
          if (permResult.state === 'granted') {
            stopPermWatch();
            handleUseCurrentLocationRef.current?.();
          }
        };
      })
      .catch(() => {}); // Permissions API unavailable — silent no-op
  }, [isNative, stopPermWatch]);

  // Auto-start watcher whenever denied state is entered on PWA
  useEffect(() => {
    if (gpsState === 'denied' && !isNative) {
      startPermWatch();
    } else {
      stopPermWatch();
    }
  }, [gpsState, isNative, startPermWatch, stopPermWatch]);

  // Clean up watcher when modal closes
  useEffect(() => {
    if (!isOpen) stopPermWatch();
  }, [isOpen, stopPermWatch]);

  // Check if location hardware is on when modal opens
  useEffect(() => {
    if (isNative) {
      // Native: check actual device GPS toggle
      checkLocationEnabled()
        .then((enabled) => setLocationOff(!enabled))
        .catch(() => {});
    } else if (navigator.permissions) {
      // Browser/PWA: check permission state via Permissions API
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          // 'denied' or 'prompt' both mean location isn't actively available
          setLocationOff(result.state !== 'granted');
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Show permission denied: pop the modal, set denied gpsState ──────────────
  const triggerDenied = useCallback((permanent = false) => {
    setIsPermanentlyDenied(permanent);
    setGpsState('denied');
    setShowPermissionModal(true);
  }, []);

  // ─── GPS flow ────────────────────────────────────────────────────────────────
  const handleUseCurrentLocation = useCallback(async () => {
    setGpsState('detecting');
    setIsPermanentlyDenied(false);
    setShowPermissionModal(false);
    setLocationOff(false);

    try {
      // ── Native extras: GPS toggle + permission ──────────────────────────────
      if (isNative) {
        // 1. GPS hardware toggle
        const locationEnabled = await checkLocationEnabled();
        if (!locationEnabled) {
          const turnedOn = await requestEnableLocation();
          if (!turnedOn) {
            triggerDenied(false);
            return;
          }
          // Give the OS a moment after turning on GPS
          await new Promise((r) => setTimeout(r, 1000));
        }

        // 2. Runtime permission
        const permResult = await ensureLocationPermission();
        if (permResult === 'permanently_denied') {
          triggerDenied(true);
          return;
        }
        if (permResult !== 'granted') {
          triggerDenied(false);
          return;
        }
      }

      // ── PWA: pre-check permission state before calling getLocation ─────────
      if (!isNative && navigator.permissions) {
        try {
          const permResult = await navigator.permissions.query({ name: 'geolocation' });
          if (permResult.state === 'denied') {
            triggerDenied(true);
            return;
          }
        } catch {
          // Permissions API unavailable — fall through to navigator.geolocation
        }
      }

      // ── Get coords (native Capacitor OR browser) ────────────────────────────
      const coords = await getLocation();
      if (!coords) {
        triggerDenied(false);
        return;
      }

      persistCoords(coords);
      dispatch(setGpsLocation(coords));
      await findNearest(coords.latitude, coords.longitude);
    } catch {
      setGpsState('error');
    }
  }, [
    isNative,
    checkLocationEnabled,
    requestEnableLocation,
    ensureLocationPermission,
    getLocation,
    dispatch,
    findNearest,
    triggerDenied,
    persistCoords,
  ]);
  handleUseCurrentLocationRef.current = handleUseCurrentLocation;

  const handleRetry = () => {
    setGpsState('idle');
    handleUseCurrentLocation();
  };

  // ─── Open app settings then re-check (native only) ───────────────────────────
  const handleGoToSettings = async () => {
    setShowPermissionModal(false);
    const result = await goToSettingsAndWait();
    if (result === 'granted') {
      handleUseCurrentLocation();
    }
  };

  const handleMapConfirm = async ({ latitude, longitude }) => {
    setShowMapPicker(false);
    setGpsState('detecting');
    persistCoords({ latitude, longitude });
    try {
      await findNearest(latitude, longitude);
    } catch {
      setGpsState('error');
    }
  };

  // ─── Inline GPS feedback block ────────────────────────────────────────────────
  const GpsFeedback = () => {
    if (gpsState === 'idle' || gpsState === 'detecting') return null;

    if (gpsState === 'denied') {
      return (
        <button
          onClick={() => setShowPermissionModal(true)}
          className="mt-2 w-full flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5 text-sm text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">{t('locationGateModal.permissionDenied')}</span>
          <span className="shrink-0 text-xs font-semibold underline underline-offset-2">{t('locationGateModal.viewHelp')}</span>
        </button>
      );
    }

    if (gpsState === 'no-area') {
      return (
        <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p>{t('locationGateModal.noAreaNearby')}</p>
            <button
              onClick={() => { setGpsState('idle'); setShowMapPicker(true); }}
              className="mt-1.5 text-xs font-semibold underline underline-offset-2"
            >
              {t('locationGateModal.tryOnMap', 'Try selecting on map')}
            </button>
          </div>
        </div>
      );
    }

    if (gpsState === 'error') {
      return (
        <div className="mt-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3 text-sm text-red-700 dark:text-red-300">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{t('locationGateModal.locationError')}</p>
            </div>
            <button
              onClick={handleRetry}
              className="shrink-0 text-xs font-semibold text-red-600 dark:text-red-400 underline underline-offset-2"
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[59]"
            onClick={isDismissable ? onSuccess : undefined}
          />

          {/* Bottom sheet */}
          <motion.div
            key="sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className="fixed inset-x-0 bottom-0 z-[60] section rounded-t-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            {locationChanged ? (
              <>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('locationGateModal.locationChangedTitle')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('locationGateModal.locationChangedSubtitle')}
                </p>
              </>
            ) : (
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('locationGateModal.title')}
              </h2>
            )}
          </div>
          {isDismissable && (
            <button
              onClick={onSuccess}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>

        {/* Options */}
        <div className="px-5 pb-8 pt-1 space-y-3">
          {/* ── Top notifications area — location-off banner OR gps feedback ── */}
          {locationOff && gpsState === 'idle' && (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-orange-800 dark:text-orange-200 leading-tight">Location not enabled</p>
                  <p className="text-xs text-orange-600 dark:text-orange-400 leading-tight mt-0.5">Enable for accurate delivery</p>
                </div>
              </div>
              <button
                onClick={handleUseCurrentLocation}
                className="shrink-0 text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg transition-colors"
              >
                Enable
              </button>
            </div>
          )}
          <GpsFeedback />

          {/* GPS option */}
          <button
            onClick={gpsState === 'detecting' ? undefined : handleUseCurrentLocation}
            disabled={gpsState === 'detecting'}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
              {gpsState === 'detecting'
                ? <Loader2 className="w-5 h-5 text-green-600 dark:text-green-400 animate-spin" />
                : <Navigation className="w-5 h-5 text-green-600 dark:text-green-400" />}
            </div>
            <div className="text-left">
              <p className="font-semibold text-green-700 dark:text-green-400 text-sm">
                {gpsState === 'detecting'
                  ? t('locationGateModal.detecting')
                  : t('locationGateModal.useCurrentLocation')}
              </p>
              <p className="text-xs text-green-600/70 dark:text-green-500/70 mt-0.5">
                {t('locationGateModal.useCurrentLocationDesc', "We'll find the nearest delivery area")}
              </p>
            </div>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500">{t('common.or', 'or')}</span>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          </div>

          {/* Map option */}
          <button
            onClick={() => { setGpsState('idle'); setShowMapPicker(true); }}
            disabled={gpsState === 'detecting'}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
              <Map className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm">
                {t('locationGateModal.selectOnMap')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('locationGateModal.selectOnMapDesc', 'Tap to pin your exact location')}
              </p>
            </div>
          </button>
        </div>
      </motion.div>

      {/* ── Permission Denied Modal ───────────────────────────────────────── */}
      {showPermissionModal && (() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isPWAInstalled =
          window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true;
        let browserInstructionKey;
        if (isPWAInstalled && isIOS) {
          browserInstructionKey = 'browserInstructionsPwaIos';
        } else if (isPWAInstalled) {
          browserInstructionKey = 'browserInstructionsPwaAndroid';
        } else if (isIOS || isSafari) {
          browserInstructionKey = 'browserInstructionsSafari';
        } else {
          browserInstructionKey = 'browserInstructionsChrome';
        }
        return (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4"
            onClick={() => setShowPermissionModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center">
                <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-full">
                  <ShieldAlert className="w-10 h-10 text-red-500" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-center text-gray-800 dark:text-gray-100">
                {t('locationGateModal.permissionDeniedTitle')}
              </h3>

              <p className="text-sm text-center text-gray-600 dark:text-gray-300 leading-relaxed">
                {isNative
                  ? t('locationGateModal.permissionDeniedNativeDesc')
                  : t(`locationGateModal.${browserInstructionKey}`)}
              </p>

              {isWatchingPermission && !isNative && !isPWAInstalled && (
                <div className="flex items-center justify-center gap-1.5 text-xs text-red-500 dark:text-red-400">
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse shrink-0" />
                  <span>{t('locationGateModal.watchingPermission')}</span>
                </div>
              )}

              <div className="space-y-2 pt-1">
                {/* Native — open app permission settings directly */}
                {isNative && (
                  <button
                    onClick={handleGoToSettings}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-md hover:from-blue-600 hover:to-blue-700 transition-all"
                  >
                    <Settings className="w-5 h-5" />
                    {t('locationGateModal.openSettings')}
                  </button>
                )}
                {/* PWA / browser — can't open system settings, so offer a retry after user follows instructions */}
                {!isNative && (
                  <button
                    onClick={() => {
                      setShowPermissionModal(false);
                      handleUseCurrentLocationRef.current?.();
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-md hover:from-blue-600 hover:to-blue-700 transition-all"
                  >
                    <Navigation className="w-5 h-5" />
                    {t('locationGateModal.tryAgain')}
                  </button>
                )}
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Map Picker */}
      {showMapPicker && (
        <MapLocationPicker
          initialCoords={lastCoords}
          onConfirm={handleMapConfirm}
          onCancel={() => setShowMapPicker(false)}
        />
      )}
        </>
      )}
    </AnimatePresence>
  );
};

export default LocationGateModal;
